/**
 * 公車路線飛線處理（前端，與 backend/utils/bus-route-geometry-sanitize.js 同步）
 */
import { GEOMETRY_SOURCES } from './bus-geometry-source.js';

const GEOMETRY_SOURCE_PRIORITY = {
    TDX_SHAPE: 1,
    OSM_RELATION: 2,
    OSM_WAY: 3,
    TDX_STOP_CHAIN: 4,
    HOOKY_MANUAL: 5
};

export const STOP_CHAIN_JUMP_M = 800;
export const SHAPE_JUMP_M = 1500;

export function haversineMeters(a, b) {
    if (!Array.isArray(a) || !Array.isArray(b)) return Infinity;
    const lon1 = Number(a[0]);
    const lat1 = Number(a[1]);
    const lon2 = Number(b[0]);
    const lat2 = Number(b[1]);
    if (![lon1, lat1, lon2, lat2].every(Number.isFinite)) return Infinity;
    const R = 6371000;
    const p1 = (lat1 * Math.PI) / 180;
    const p2 = (lat2 * Math.PI) / 180;
    const dphi = ((lat2 - lat1) * Math.PI) / 180;
    const dl = ((lon2 - lon1) * Math.PI) / 180;
    const h =
        Math.sin(dphi / 2) ** 2 +
        Math.cos(p1) * Math.cos(p2) * Math.sin(dl / 2) ** 2;
    return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
}

export function jumpThresholdMeters(source) {
    return source === GEOMETRY_SOURCES.TDX_STOP_CHAIN ? STOP_CHAIN_JUMP_M : SHAPE_JUMP_M;
}

function linePartsFromGeometry(geom) {
    if (!geom) return [];
    const coords = geom.coordinates;
    if (geom.type === 'LineString') return Array.isArray(coords) ? [coords] : [];
    if (geom.type === 'MultiLineString') return Array.isArray(coords) ? coords : [];
    return [];
}

export function splitLineCoordsAtJumps(coords, maxJumpM) {
    if (!Array.isArray(coords) || coords.length < 2) return [];
    const parts = [];
    let current = [coords[0]];
    for (let i = 1; i < coords.length; i += 1) {
        const d = haversineMeters(coords[i - 1], coords[i]);
        if (d > maxJumpM) {
            if (current.length >= 2) parts.push(current);
            current = [coords[i]];
        } else {
            current.push(coords[i]);
        }
    }
    if (current.length >= 2) parts.push(current);
    return parts;
}

function isDisplayPrepared(props = {}) {
    if (props.h_display_prepared === true) return true;
    if (props.h_scale_band) return true;
    return typeof props.h_slim_algo === 'string' && props.h_slim_algo.startsWith('bus_scale');
}

function shouldPreserveSlimmedGeometry(geom, props = {}) {
    if (isDisplayPrepared(props)) return true;
    const rings = linePartsFromGeometry(geom);
    let pts = 0;
    for (const ring of rings) {
        if (Array.isArray(ring)) pts += ring.length;
    }
    return pts >= 8;
}

export function sanitizeBusRouteGeometry(geom, props = {}) {
    if (!geom) return null;
    if (shouldPreserveSlimmedGeometry(geom, props)) {
        return busRouteHasDrawableGeometry({ geometry: geom }) ? geom : null;
    }
    const maxJump = jumpThresholdMeters(props.h_geometry_source);
    const parts = [];
    for (const ring of linePartsFromGeometry(geom)) {
        parts.push(...splitLineCoordsAtJumps(ring, maxJump));
    }
    if (!parts.length) return null;
    if (parts.length === 1) return { type: 'LineString', coordinates: parts[0] };
    return { type: 'MultiLineString', coordinates: parts };
}

export function busRouteHasDrawableGeometry(feature) {
    const geom = feature?.geometry;
    if (!geom) return false;
    if (geom.type === 'LineString') {
        return Array.isArray(geom.coordinates) && geom.coordinates.length >= 2;
    }
    if (geom.type === 'MultiLineString') {
        return (geom.coordinates || []).some((ring) => Array.isArray(ring) && ring.length >= 2);
    }
    return false;
}

export function routeAuthorityKey(props = {}) {
    const uid = props.h_ref_codes?.tdx_route_uid;
    if (uid) return `uid:${uid}`;
    const name = String(props.name || props.ref || props.h_route_name || '').trim();
    if (!name) return null;
    const dir = props.h_direction == null ? '' : String(props.h_direction);
    return `name:${name}|d:${dir}`;
}

function isBetterThanStopChain(source) {
    const pri = GEOMETRY_SOURCE_PRIORITY[source];
    return Number.isFinite(pri) && pri < GEOMETRY_SOURCE_PRIORITY.TDX_STOP_CHAIN;
}

export function collectBetterGeometryKeys(features = []) {
    const keys = new Set();
    for (const f of features) {
        const p = f?.properties || {};
        if (!isBetterThanStopChain(p.h_geometry_source)) continue;
        const key = routeAuthorityKey(p);
        if (key) keys.add(key);
    }
    return keys;
}

export function preferAuthoritativeBusRoutes(features = [], betterKeys) {
    const keys = betterKeys instanceof Set ? betterKeys : collectBetterGeometryKeys(features);
    return features.filter((f) => {
        const p = f?.properties || {};
        if (p.h_geometry_source !== GEOMETRY_SOURCES.TDX_STOP_CHAIN) return true;
        const key = routeAuthorityKey(p);
        return !key || !keys.has(key);
    });
}

export function sanitizeBusRouteFeature(feature) {
    if (!feature) return null;
    const geom = sanitizeBusRouteGeometry(feature.geometry, feature.properties || {});
    if (!geom) return null;
    if (geom === feature.geometry) return feature;
    return { ...feature, geometry: geom };
}

export function prepareBusRoutesForDisplay(features = [], allFeaturesForKeys = null) {
    const betterKeys = collectBetterGeometryKeys(allFeaturesForKeys || features);
    const out = [];
    for (const f of preferAuthoritativeBusRoutes(features, betterKeys)) {
        const next = sanitizeBusRouteFeature(f);
        if (next && busRouteHasDrawableGeometry(next)) out.push(next);
    }
    return out;
}
